import { useState, useEffect, useRef } from 'preact/hooks';

interface UseImageLoaderOptions {
  rootMargin?: string;
  threshold?: number;
  lowQualityPlaceholder?: string;
}

export function useImageLoader(
  src: string,
  options: UseImageLoaderOptions = {}
) {
  const {
    rootMargin = '50px',
    threshold = 0.01,
    lowQualityPlaceholder,
  } = options;

  const [imageSrc, setImageSrc] = useState<string>(lowQualityPlaceholder || '');
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (!imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Start loading the image
            const img = new Image();
            
            img.onload = () => {
              setImageSrc(src);
              setIsLoading(false);
              setIsError(false);
            };
            
            img.onerror = () => {
              setIsLoading(false);
              setIsError(true);
            };
            
            img.src = src;
            observer.disconnect();
          }
        });
      },
      {
        rootMargin,
        threshold,
      }
    );

    observer.observe(imgRef.current);

    return () => {
      observer.disconnect();
    };
  }, [src, rootMargin, threshold]);

  return {
    imgRef,
    imageSrc,
    isLoading,
    isError,
  };
}